import { Container, Row, Col, Button, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import Feedback from 'react-bootstrap/esm/Feedback';
import { FaBuilding, FaChartLine, FaUsers, FaHandshake } from 'react-icons/fa';
import { TextFormInput, TextAreaFormInput } from '@/components';
import { Controller } from 'react-hook-form';
import InquiryFormExtras from '@/components/InquiryFormExtras';
import useSiteInquiryForm from '@/hooks/useSiteInquiryForm';

const HoteliersSection = () => {
  const {
    control,
    onSubmit,
    status,
    error,
    confirmation,
    submitting,
    resetStatus,
    websiteField,
    setWebsiteField,
    turnstile,
    showTurnstileConfigWarning,
  } = useSiteInquiryForm({
    formType: 'hotelier',
    action: 'hotelier_inquiry',
    defaultValues: {
      hotelName: '',
      location: '',
      contactName: '',
      email: '',
      phone: '',
      numberOfRooms: '',
      message: ''
    },
    mapValues: (values) => ({
      company_name: values.hotelName,
      hotel_name: values.hotelName,
      contact_name: values.contactName,
      email: values.email,
      phone: values.phone,
      message: values.message,
      extra: {
        location: values.location,
        number_of_rooms: values.numberOfRooms,
      },
    }),
  });

  return (
    <section className="hoteliers-section py-5 py-lg-6">
      <Container>
        <Row className="mb-5">
          <Col lg={12} className="text-center">
            <h2 className="sec-title mb-3">Join Our Team</h2>
            <p className="lead text-muted mb-4">
              Are you a hotelier or apartment complex owner? Partner with us to promote your property and reach thousands of travelers.
            </p>
            
            {/* Icons under title */}
            <Row className="g-4 justify-content-center mb-4">
              <Col xs={6} md={4} lg={3}>
                <div className="text-center">
                  <div className="hoteliers-icon-wrapper mb-3">
                    <FaBuilding className="hoteliers-icon" />
                  </div>
                  <h5 className="mb-0">Property Promotion</h5>
                </div>
              </Col>
              <Col xs={6} md={4} lg={3}>
                <div className="text-center">
                  <div className="hoteliers-icon-wrapper mb-3">
                    <FaChartLine className="hoteliers-icon" />
                  </div>
                  <h5 className="mb-0">Increased Bookings</h5>
                </div>
              </Col>
              <Col xs={6} md={4} lg={3}>
                <div className="text-center">
                  <div className="hoteliers-icon-wrapper mb-3">
                    <FaUsers className="hoteliers-icon" />
                  </div>
                  <h5 className="mb-0">Marketing Support</h5>
                </div>
              </Col>
              <Col xs={6} md={4} lg={3}>
                <div className="text-center">
                  <div className="hoteliers-icon-wrapper mb-3">
                    <FaHandshake className="hoteliers-icon" />
                  </div>
                  <h5 className="mb-0">Fair Partnership</h5>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="align-items-start">
          <Col lg={6}>
            <div className="hoteliers-content">
              <h3 className="h4 fw-semibold mb-3">Why Partner With Us?</h3>
              <p className="mb-4">
                As a leading travel agency in Crete, we help hoteliers and apartment complex owners 
                reach a wider audience and increase their bookings. We provide comprehensive marketing 
                support, professional photography, and dedicated account management.
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-2">✓</span>
                  <span>Professional property listing on our platform</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-2">✓</span>
                  <span>Marketing and promotional support</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-2">✓</span>
                  <span>Access to our extensive customer base</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-2">✓</span>
                  <span>Fair and transparent partnership terms</span>
                </li>
                <li className="mb-3 d-flex align-items-start">
                  <span className="text-primary me-2">✓</span>
                  <span>Dedicated support team</span>
                </li>
              </ul>
            </div>
          </Col>
          <Col lg={6}>
            <div className="hoteliers-form-wrapper">
              <h3 className="h4 fw-semibold mb-4">Get Started Today</h3>
              {status === 'success' ? (
                <InquiryFormExtras
                  status={status}
                  confirmation={confirmation}
                  onReset={resetStatus}
                />
              ) : (
              <form onSubmit={onSubmit} className="hoteliers-contact-form" noValidate>
                <div className="row g-3">
                  <TextFormInput
                    name="hotelName"
                    label="Hotel/Apartment Name *"
                    containerClass="col-md-12"
                    control={control}
                    rules={{ required: 'Hotel name is required' }}
                  />
                  <FormGroup className="col-md-12">
                    <FormLabel>Location *</FormLabel>
                    <Controller
                      name="location"
                      control={control}
                      rules={{ required: 'Location is required' }}
                      render={({ field, fieldState }) => (
                        <>
                          <FormControl
                            as="select"
                            {...field}
                            isInvalid={Boolean(fieldState.error?.message)}
                          >
                            <option value="">Select a location</option>
                            <option value="Ierapetra">Ierapetra</option>
                            <option value="Makrigialos">Makrigialos</option>
                            <option value="Agios Nikolaos">Agios Nikolaos</option>
                            <option value="Sitia">Sitia</option>
                          </FormControl>
                          {fieldState.error?.message && (
                            <Feedback type="invalid">{fieldState.error.message}</Feedback>
                          )}
                        </>
                      )}
                    />
                  </FormGroup>
                  <TextFormInput
                    name="contactName"
                    label="Your Name *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{ required: 'Name is required' }}
                  />
                  <TextFormInput
                    name="phone"
                    label="Phone *"
                    containerClass="col-md-6"
                    control={control}
                    rules={{ required: 'Phone is required' }}
                  />
                  <TextFormInput
                    name="email"
                    label="Email *"
                    containerClass="col-md-12"
                    control={control}
                    rules={{ 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    }}
                  />
                  <TextFormInput
                    name="numberOfRooms"
                    label="Number of Rooms/Units"
                    containerClass="col-md-12"
                    control={control}
                    type="number"
                  />
                  <TextAreaFormInput
                    name="message"
                    label="Additional Information"
                    containerClass="col-12"
                    rows={4}
                    control={control}
                    placeholder="Tell us about your property, amenities, target market, etc."
                  />
                  <Col xs={12}>
                    <InquiryFormExtras
                      honeypotId="hotelier-website-field"
                      websiteField={websiteField}
                      setWebsiteField={setWebsiteField}
                      turnstileRef={turnstile.containerRef}
                      showTurnstileConfigWarning={showTurnstileConfigWarning}
                      error={error}
                      status={status}
                    />
                  </Col>
                  <Col xs={12}>
                    <Button variant="primary" type="submit" className="w-100 btn-lg" disabled={submitting}>
                      {submitting ? 'Sending…' : 'Submit Application'}
                    </Button>
                  </Col>
                </div>
              </form>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default HoteliersSection;

